// SPDX-License-Identifier: AGPL-3.0-or-later

import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/features/app/utils/LimitUtils';
import * as CustomSoundDB from '@app/features/notification/utils/CustomSoundDB';
import {SoundType} from '@app/features/notification/utils/SoundUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import VoiceConnectionManager from '@app/features/voice/engine/VoiceConnectionManager';
import {getEffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import type {LocalTrack, LocalTrackPublication, Room} from 'livekit-client';
import {Track} from 'livekit-client';

const logger = new Logger('VoiceEntranceSoundManager');
export const VOICE_ENTRANCE_SOUND_TRACK_NAME = 'entrance-sound';
export async function playEntranceSound(room: Room | null): Promise<void> {
	const hasEntranceSounds = isLimitToggleEnabled(
		{feature_voice_entrance_sounds: LimitResolver.resolve({key: 'feature_voice_entrance_sounds', fallback: 0})},
		'feature_voice_entrance_sounds',
	);
	if (!hasEntranceSounds) {
		SoundCommands.playSound(SoundType.UserJoin);
		return;
	}
	try {
		if (getEffectiveAudioState().effectiveMute) {
			SoundCommands.playSound(SoundType.UserJoin);
			return;
		}
		const {sound: entranceSound} = await CustomSoundDB.getResolvedEntranceSound(VoiceConnectionManager.guildId);
		if (!entranceSound) {
			SoundCommands.playSound(SoundType.UserJoin);
			return;
		}
		if (!room?.localParticipant) {
			SoundCommands.playSound(SoundType.UserJoin);
			return;
		}
		const audioContext = new AudioContext();
		if (audioContext.state === 'suspended') {
			await audioContext.resume().catch((e) => logger.warn('AudioContext.resume failed', e));
		}
		const participant = room.localParticipant;
		let publishedTrack: LocalTrack | null = null;
		let publication: LocalTrackPublication | null = null;
		try {
			const arrayBuffer = await entranceSound.blob.arrayBuffer();
			const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
			const source = audioContext.createBufferSource();
			source.buffer = audioBuffer;
			source.connect(audioContext.destination);
			const dest = audioContext.createMediaStreamDestination();
			source.connect(dest);
			const audioTrack = dest.stream.getAudioTracks()[0];
			if (!audioTrack) {
				SoundCommands.playSound(SoundType.UserJoin);
				return;
			}
			publication = await participant.publishTrack(audioTrack, {
				name: VOICE_ENTRANCE_SOUND_TRACK_NAME,
				source: Track.Source.Microphone,
				dtx: false,
				red: true,
			});
			publishedTrack = publication.track ?? null;
			await new Promise<void>((resolve) => {
				source.onended = () => resolve();
				source.start();
			});
			logger.info('Custom entrance sound played');
		} finally {
			try {
				if (publishedTrack) {
					await participant.unpublishTrack(publishedTrack);
				} else if (publication?.trackName === VOICE_ENTRANCE_SOUND_TRACK_NAME) {
					const pubs = Array.from(participant.audioTrackPublications.values());
					const pub = pubs.find((p) => p.trackName === VOICE_ENTRANCE_SOUND_TRACK_NAME);
					if (pub?.track) {
						await participant.unpublishTrack(pub.track);
					}
				}
			} catch (e) {
				logger.error('Failed to unpublish entrance-sound track', e);
			}
			await audioContext.close().catch((e) => logger.warn('AudioContext.close failed', e));
		}
	} catch (e) {
		logger.error('Failed', e);
		SoundCommands.playSound(SoundType.UserJoin);
	}
}
